import { useState, useEffect, useRef } from "react";
import { Logo } from "./Logo";
import { RobotHead3D } from "./RobotHead3D";
import { SimulacionRango } from "./SimulacionRango";
import { BotonAyuda } from "./BotonAyuda";
import { PacmanBot } from "./PacmanBot";
import { DexSoportadas } from "./DexSoportadas";
import { type VaultPublico } from "../hooks/useFlota";
import { useT } from "../lib/i18n";
import "../landing.css";

/**
 * Al hacer scroll, cada bloque con `.reveal` entra desde abajo una sola vez. Es el efecto
 * `rowin`/`reveal` de scalpex: un IntersectionObserver le pone `.in` y el CSS hace el resto.
 */
function useReveal() {
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const nodo = raiz.current;
    if (!nodo) return;
    const objetivos = Array.from(nodo.querySelectorAll<HTMLElement>(".reveal"));
    // Lo que YA está a la vista al cargar (el hero, above-the-fold) se muestra al instante, sin
    // esperar al observer. Antes el hero entero —robot incluido— podía quedarse en blanco si el
    // IntersectionObserver tardaba en disparar (bundle pesado, equipo lento). El resto anima al hacer scroll.
    const vh = window.innerHeight || 0;
    objetivos.forEach((e) => { if (e.getBoundingClientRect().top < vh * 0.9) e.classList.add("in"); });
    if (!("IntersectionObserver" in window)) {
      objetivos.forEach((e) => e.classList.add("in"));
      return;
    }
    const obs = new IntersectionObserver(
      (filas) => filas.forEach((f) => f.isIntersecting && f.target.classList.add("in")),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );
    objetivos.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, []);
  return raiz;
}

/** Lo que la cinta va repitiendo: la promesa de la marca, en trozos cortos. */
const CINTA = [
  "No custodial", "El rango se reajusta solo", "Comisión 10% — solo de ganancias",
  "Uniswap V3", "PancakeSwap", "PRJX", "Aerodrome", "6 redes", "Tu dinero nunca sale de tu vault",
];

/** Los tres pasos de «Cómo funciona», visibles en la portada. */
const PASOS = [
  { titulo: "Crea tu vault y deposita",
    texto: "Eliges la pool y el rango. El vault es un contrato a tu nombre: solo tu wallet puede sacar dinero de él." },
  { titulo: "Kel lo mantiene en rango 24/7",
    texto: "Cuando el precio se sale, reajusta el rango — solo si compensa el gas. Si el mercado cae de golpe no vende barato: abre un rango de recuperación." },
  { titulo: "Cobras cuando quieras",
    texto: "Las comisiones se acumulan en tu posición. Las retiras cuando quieras, o «Sacar todo» en una sola transacción. Keellabs se queda el 10% de las comisiones, nunca del capital." },
] as const;

/** [pregunta, respuesta] — mismos textos que tenía el modal, ya traducidos. */
const RIESGOS = [
  ["Puedes perder dinero.", "Si el activo cae y no vuelve, la recuperación no tiene de dónde recuperar: el rango se queda esperando un rebote que puede no llegar."],
  ["Cada reajuste cuesta gas", "y sale de la bolsa que tú cargas. Un rango estrecho cobra más comisiones pero se sale más veces, y cada salida cuesta."],
  ["Los contratos no tienen auditoría externa.", "Están probados y el código se puede leer, pero eso no es lo mismo."],
  ["El código de un vault no cambia solo.", "Cuando publicamos una versión nueva, tú decides si te pasas a ella. Nadie puede hacerlo por ti."],
] as const;

const FAQ = [
  ["¿Podéis quedaros con mi dinero?", "No. El vault es tuyo y solo tu wallet puede sacar de él. El robot únicamente puede reajustar el rango."],
  ["¿Cuándo cobro?", "Cuando quieras. Las comisiones se acumulan en la posición y el botón de retirar las cobra y te las manda en la misma transacción."],
  ["¿Qué cobra Keellabs?", "El 10% de las comisiones que genera tu posición. Nunca del capital, y está topado en el contrato: se puede bajar, no subir."],
  ["¿Por qué solo unas pocas pools?", "Porque el vault necesita que el oráculo de la pool responda para no operar con un precio manipulable. Muchas pools con fondos no lo tienen."],
  ["¿Y si quiero salirme?", "«Sacar todo» cierra la posición y te devuelve capital, ganancias y el gas sin gastar, en una transacción."],
] as const;

/**
 * La portada. Su unico trabajo es que alguien que llega sin saber nada entienda QUE hace esto y
 * compruebe que es real, en ese orden.
 *
 * El heroe no explica: enseña. La banda deriva, el precio se sale por abajo y el rango se estira
 * hacia donde tiene que volver — que es literalmente lo que hace `_recover` en el contrato. Un
 * parrafo tarda mas y convence menos.
 */
/**
 * @param yaConectado A la portada se llega tambien desde el logo con la wallet ya puesta. Ahi
 *                    "Conectar wallet" no significa nada: el boton tiene que devolverte a tus
 *                    piscinas, que es lo que de verdad quieres al volver.
 */
export function Landing({
  alConectar, conectando, yaConectado = false,
}: {
  alConectar: () => void;
  conectando: boolean;
  yaConectado?: boolean;
  /** Se mantiene por compatibilidad con App; la lista pública se movió al Dashboard. */
  alCopiar?: (v: VaultPublico) => void;
}) {
  const t = useT();
  const [info, setInfo] = useState<null | "como">(null);
  const raiz = useReveal();
  // Cómo funciona, riesgos y preguntas viven ahora en la portada (antes solo en un modal que casi
  // nadie abría): los botones del héroe y del pie bajan a su sección.
  const irA = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="lp" ref={raiz}>
      <PacmanBot />

      <section className="heroe-centro reveal">
        <div className="robot3d-wrap">
          <RobotHead3D />
        </div>
        <p className="rotulo">{t("Rendimiento LP automático · No custodial")}</p>
        <h1 className="titular">
          {t("Gana comisiones")} <em>{t("en piloto automático")}</em>
        </h1>
        <p className="sub">
          {t("Cobra las comisiones de cada operación")} <b>{t("sin estar pendiente del precio")}</b>. {t("El robot mantiene tu liquidez donde se gana y la reajusta sola cuando el mercado se mueve — y tu dinero")} <b>{t("nunca sale de tu vault")}</b>.
        </p>
        <div className="acciones">
          <button className="btn principal cta" onClick={alConectar} disabled={conectando}>
            <span>{conectando ? t("Conectando…") : yaConectado ? t("Ir a mis piscinas") : t("Empezar a ganar")}</span>
            <svg className="cta-flecha" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" />
            </svg>
          </button>
          <button className="btn cta-2" onClick={() => irA("como")}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><polygon points="10 8.5 16 12 10 15.5" fill="currentColor" stroke="none" />
            </svg>
            <span>{t("Cómo funciona")}</span>
          </button>
        </div>
      </section>

      {/* Cinta de marca que se desliza — el efecto scrollx de scalpex. Se duplica el contenido
          para que el bucle sea continuo (la animacion desplaza justo la mitad). */}
      <div className="marquee reveal" aria-hidden>
        <div className="marquee-pista">
          {[...CINTA, ...CINTA].map((frase, i) => <span key={i}>{t(frase)}</span>)}
        </div>
      </div>

      {/* El gráfico del rebalanceo: la banda con el rango y la aguja que se sale y vuelve. Es el
          «enseña, no cuentes» del producto, así que va lo primero bajo el robot. */}
      <section className="lp-rebal reveal">
        <div className="lp-rebal-txt">
          <p className="rotulo">{t("Cero estrés • Reacción inmediata")}</p>
          <h2 className="lp-h2">
            {t("Si el mercado cambia, la")} <em>{t("Inteligencia Artificial")}</em> {t("se adapta por ti")}
          </h2>
          <p className="sub">
            {t("Olvídate de perder oportunidades por no estar frente a la pantalla. Si los precios suben o bajan de golpe, Kel hace los cálculos matemáticos al instante y reubica tu dinero donde es más rentable. Él hace el trabajo pesado para que nunca dejes de cobrar comisiones.")}
          </p>
        </div>
        <div className="lp-rebal-demo">
          <SimulacionRango />
        </div>
      </section>

      <section className="lp-pasos reveal" id="como">
        <p className="rotulo">{t("Cómo funciona")}</p>
        <h2 className="lp-h2">{t("Tres pasos y")} <em>{t("Kel hace el resto")}</em></h2>
        <ol className="lp-pasos-lista">
          {PASOS.map((p, i) => (
            <li key={p.titulo} className="lp-paso">
              <span className="lp-paso-n">{i + 1}</span>
              <h3>{t(p.titulo)}</h3>
              <p>{t(p.texto)}</p>
            </li>
          ))}
        </ol>
        <button className="btn-texto" onClick={() => setInfo("como")}>{t("Ver la explicación completa")} →</button>
      </section>

      <DexSoportadas />

      <section className="lp-dudas reveal">
        <div id="riesgos">
          <p className="rotulo">{t("Riesgos")}</p>
          <h2 className="lp-h2">{t("Lo que tienes que saber")}</h2>
          <div className="acordeon">
            {RIESGOS.map(([q, r]) => (
              <details key={q}><summary>{t(q)}</summary><p>{t(r)}</p></details>
            ))}
          </div>
        </div>
        <div id="faq">
          <p className="rotulo">{t("Preguntas frecuentes")}</p>
          <h2 className="lp-h2">{t("Dudas habituales")}</h2>
          <div className="acordeon">
            {FAQ.map(([q, r]) => (
              <details key={q}><summary>{t(q)}</summary><p>{t(r)}</p></details>
            ))}
          </div>
        </div>
      </section>

      <footer className="pie reveal">
        <div className="pie-fila">
          <div className="pie-marca">
            <span className="pie-logo"><Logo size={26} /></span>
            <div>
              <strong>Keellabs</strong>
              <span className="pie-tag">{t("No custodial · tu dinero nunca sale de tu vault")}</span>
            </div>
          </div>

          <nav className="pie-nav" aria-label={t("Enlaces del pie")}>
            <button onClick={() => irA("como")}>{t("Cómo funciona")}</button>
            <button onClick={() => irA("riesgos")}>{t("Riesgos")}</button>
            <button onClick={() => irA("faq")}>{t("Preguntas frecuentes")}</button>
            <a href="/terminos.html">{t("Términos")}</a>
            <a href="/privacidad.html">{t("Privacidad")}</a>
          </nav>

          <div className="pie-social">
            <a className="pie-ico" href="https://x.com/keelagentlabs" target="_blank" rel="noopener noreferrer" title="X (Twitter)" aria-label="X">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a className="pie-ico" href="https://github.com/KeelLabsYield" target="_blank" rel="noopener noreferrer" title="GitHub" aria-label="GitHub">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0022 12.017C22 6.484 17.522 2 12 2z" /></svg>
            </a>
            <a className="pie-ico" href="https://t.me/keellabs" target="_blank" rel="noopener noreferrer" title="Telegram" aria-label="Telegram">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" /></svg>
            </a>
            <span className="pie-copy">© 2026 Keellabs</span>
          </div>
        </div>
      </footer>

      {info && <Info que={info} cerrar={() => setInfo(null)} />}

      <BotonAyuda />
    </div>
  );
}

function Info({ que, cerrar }: { que: "como"; cerrar: () => void }) {
  const t = useT();
  const contenido = {
    como: {
      titulo: t("Cómo funciona"),
      cuerpo: (
        <>
          <p>
            <strong>{t("1. El motor de ganancias.")}</strong> {t("Aportas liquidez definiendo un rango de precios en los principales DEX del mercado (Uniswap, Aerodrome, PancakeSwap o Project X). Operamos de forma nativa en redes como Arbitrum, Robinhood Chain e Hyperliquid, permitiéndote cobrar comisiones por transacciones tanto de criptomonedas como de acciones tokenizadas reales (como el ETF QQQ).")}
          </p>
          <p>
            <strong>{t("2. Automatización 24/7.")}</strong> {t("Hacer esto a mano es ineficiente; si el precio sale de tu rango, dejas de ganar plata. Kel, nuestro agente de Inteligencia Artificial, monitorea la blockchain sin descanso y rebalancea tu posición automáticamente solo cuando es matemáticamente rentable.")}
          </p>
          <p>
            <strong>{t("3. Estrategia inteligente (Anti-Pérdidas).")}</strong> {t("Si el mercado cae de golpe, el algoritmo NO re-centra a ciegas. Hacerlo significaría vender barato y asumir pérdidas (Impermanent Loss). En su lugar, la IA calcula un rango asimétrico hacia arriba, diseñado específicamente para recuperar tu capital original a medida que el precio rebota.")}
          </p>
          <p>
            <strong>{t("4. Custodia Inquebrantable.")}</strong> {t("Confía en el código, no en promesas. Por contrato inteligente, la IA solo tiene permiso para ajustar rangos. Jamás podrá retirar tus fondos ni tocar tus rendimientos. La liquidez permanece 100% bajo tu control en todo momento.")}
          </p>
        </>
      ),
    },
  }[que];

  return (
    <div className="telon" onClick={cerrar} role="dialog" aria-modal>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{contenido.titulo}</h2>
        {contenido.cuerpo}
        <button className="btn" onClick={cerrar}>{t("Entendido")}</button>
      </div>
    </div>
  );
}
